<script setup lang="ts">
import { MarketType, Currency, Scarcity, Auction, NftSkill } from "fungi-types";
import { computed } from "vue";
import { Gavel } from "lucide-vue-next";
import OvTicker from "~/components/common/OvTicker.vue";
import useConvert from "~/composables/useConvert";

const { t } = useI18n();

const { getAuctionAmount } = useConvert;

import { useStoreUserV2 } from "~/stores/storeUserV2";
import { useI18n } from "vue-i18n";

const storeUser = useStoreUserV2();

const props = withDefaults(
  defineProps<{
    auction: Auction;
    scarcity?: Scarcity;
    useCurrency?: Currency;
    stock?: number;
    ownedBy?: NftSkill["ownedBy"];
  }>(),
  {
    scarcity: Scarcity.SILVER,
  }
);

const endedAt = computed(() => {
  return props.auction.endedAt ? new Date(props.auction.endedAt) : new Date();
});

const emits = defineEmits<{ (event: "time-out"): void }>();
const handleTimeoutEvent = () => emits("time-out");

const highlightBidderName = computed(() => {
  return storeUser.username === props.auction.lastBidder?.username;
});

const highLightSellerName = computed(() => {
  return storeUser.username === props.ownedBy?.username;
});

const currency = computed(() => {
  return props.useCurrency ?? storeUser.preferredCurrency ?? Currency.USD;
});

const amountInPreferredCurrency = computed(() => {
  const preferredCurrency = props.useCurrency ?? storeUser.preferredCurrency ?? Currency.USD;
  if (preferredCurrency === Currency.USD) return amount.value.fiat.usd;
  if (preferredCurrency === Currency.EUR) return amount.value.fiat.eur;
  return amount.value.crypto.wpol;
});

const marketType = computed(() => {
  return props.auction.marketType === MarketType.SECONDARY
    ? MarketType.SECONDARY
    : MarketType.PRIMARY;
});

const amount = computed(() => {
  const resp = getAuctionAmount({
    scarcity: props.scarcity!,
    marketType: marketType.value,
    usdcAmount: props.auction.startUsdcAmount || "0",
    weiAmount: props.auction.lastBidAmount || props.auction.startedAmount || "0",
  });

  return {
    ...resp,
  };
});

const secondaryAmount = computed(() => {
  if (props.auction.marketType !== MarketType.SECONDARY) return 0;
  if (props.auction.currency === Currency.USDC) {
    return amount.value.fiat.usd;
  } else {
    return amount.value.crypto.wpol;
  }
});
</script>

<template>
  <div class="w-full flex flex-col gap-0.5 cursor-default">
    <div class="w-full flex justify-between">
      <div v-if="auction.marketType !== MarketType.SECONDARY" class="flex gap-2">
        <div class="text-lg gap-1 flex font-semibold">
          <span>{{ amountInPreferredCurrency }}</span>
          <span class="font-semibold">
            <template v-if="currency === Currency.EUR">€</template>
            <template v-else>$</template>
          </span>
        </div>
      </div>
      <div
        v-else-if="auction.marketType === MarketType.SECONDARY"
        class="text-lg gap-2 flex items-center"
      >
        <div class="flex items-center gap-1">
          <span>{{ secondaryAmount }}</span>
          <span v-if="auction.currency === Currency.USDC">$</span>
          <icon-fg-w-pol v-else class="w-f3 h-f3" />
        </div>
        <div
          v-if="auction.currency !== Currency.USDC"
          class="flex items-center text-slate-300 text-sm gap-1 mt-[1px]"
        >
          <span>{{ amount.fiat.usd }}</span>
          <span class="font-semibold"> $ </span>
        </div>
        <div v-else class="flex items-center text-slate-300 text-sm gap-1 mt-[1px]">
          <span>{{ amountInPreferredCurrency }}</span>
          <span class="font-semibold">
            <template v-if="currency === Currency.EUR">€</template>
            <template v-else>$</template>
          </span>
        </div>
      </div>
      <Tooltip v-if="auction.countOrders" :text="t('auctions.bids-number')">
        <div class="flex whitespace-nowrap items-center text-xs gap-0.5 h-full">
          {{ auction.countOrders }}<Gavel class="w-3 h-3 mb-0.5" />
        </div>
      </Tooltip>
      <Tooltip v-if="stock !== undefined" :text="t('auctions.bids-number')">
        <div class="flex whitespace-nowrap items-center text-xs gap-0.5 h-full text-neutral-400">
          Stock {{ stock }}
        </div>
      </Tooltip>
    </div>
    <div
      v-if="stock === undefined"
      class="w-full flex items-center gap-2 justify-between text-slate-300 text-xs"
    >
      <OvTicker
        @time-out="handleTimeoutEvent"
        :cuid="auction.cuid"
        :end-date="endedAt"
        :status="auction.status"
        class="whitespace-nowrap"
      />
      <div
        class="grow flex flex-1 justify-end items-center gap-1 w-full overflow-hidden text-ellipsis"
      >
        <Tooltip
          v-if="auction.marketType === MarketType.SECONDARY && ownedBy"
          :text="t('auctions.seller', 'Last bidder')"
        >
          <p
            :class="[highLightSellerName && 'highlightName']"
            class="overflow-hidden text-ellipsis max-w-16"
          >
            {{ ownedBy.username }}
          </p>
        </Tooltip>
        <Tooltip v-else :text="t('auctions.last-bidder', 'Last bidder')">
          <p
            :class="[highlightBidderName && 'highlightName']"
            class="overflow-hidden text-ellipsis max-w-16"
          >
            {{ auction.lastBidder?.username }}
          </p>
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.highlightName {
  @apply font-medium text-yellow-40;
}
</style>
